import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import {
  RefundReleaseUserLiteModel,
  useGetRefundReleaseUsers,
} from 'api-hooks/refund/refund-release-user';
import { AuthorizationRules } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import RefundReleaseUserTable from './components/table';
import ReleasePillStatus from '../components/release-pill-status';
import ReleaseTabList from '../components/release-tab-list';

export default function RefundReleaseUserList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );
  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<RefundReleaseUserLiteModel>[]>(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 150,
        cell: ({ getValue }) => (
          <ReleasePillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('refund:requested_at'),
        accessorKey: 'requestedAt',
        minSize: 80,
        sortName: 'requested_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:number'),
        accessorKey: 'number',
        minSize: 150,
      },
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        minSize: 150,
        sortName: 'invoice_number',
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 150,
        sortName: 'customer',
      },
      {
        header: t('common:amount'),
        accessorKey: 'amount',
        minSize: 150,
        sortName: 'amount',
        textAlign: 'right',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('refund:refund_reason'),
        accessorKey: 'reason',
        minSize: 250,
        sortName: 'reason',
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue(),
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetRefundReleaseUsers,
    {
      params: {
        page,
        filter: { status } as unknown as string,
        // status,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.ReleaseSalesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:refund_release_user')}
          showAdd={false}
          onCreate={() => {}}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
        />
        <ReleaseTabList
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <RefundReleaseUserTable
          refundReleaseUser={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
